import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchMyAccessTree } from "@/services/apis/useCaseApi";
import { useAuth } from "@/contexts/AuthContext";
import type { UseCase } from "@/types/use-case.types";

/**
 * Base react-query key for the use-case access tree. Real cache entries hang off
 * it per user (`myAccessTreeKey`); this bare form is the prefix used to
 * invalidate every user's entry at once.
 */
export const MY_ACCESS_TREE_KEY = ["use-cases", "me"] as const;

/**
 * Per-user access-tree key. The userId is part of the key because the query
 * cache outlives a same-tab user switch — logout navigates in-SPA and never
 * clears react-query — so a shared key would serve the previous user's tree
 * (and let the boot prefetch skip the refetch) until staleTime lapsed.
 */
export const myAccessTreeKey = (userId: string | number | undefined) =>
  [...MY_ACCESS_TREE_KEY, userId ?? null] as const;

/**
 * Fetches the current user's dashboard access tree (`GET /use-cases/me`): the use
 * cases their company is entitled to, each narrowed to the dashboards shared with
 * them. Drives the two-tier dashboards nav and the route guard.
 *
 * Uses react-query so the tree is cached/de-duplicated across the nav and the
 * view guard instead of each firing its own request.
 *
 * @param enabled - gate the query until the user is authenticated.
 */
export function useMyAccessTree(enabled = true) {
  // Every consumer (AppRail, UseCasesHome, UseCaseDashboardView) mounts under
  // AuthProvider, and AuthContext does not import this hook — no cycle.
  const { userDetails, currentUser } = useAuth();
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: myAccessTreeKey(userDetails?.userId),
    queryFn: fetchMyAccessTree,
    // Without a userId the key would collapse to a shared "anonymous" entry, so
    // stay idle until the session is known.
    //
    // `currentUser` is required as well, and it is what keeps this off the wire
    // during boot. `userDetails` comes from the `saras_user_details` COOKIE, which
    // is readable synchronously at mount and survives from the PREVIOUS session —
    // so on a fresh login this query would otherwise fire ~1s before Firebase has
    // restored a user. The request interceptor can only mint a missing token when
    // `auth.currentUser` exists, so that early call went out with no Authorization
    // header at all and came back 403 (verified on dev: `/use-cases/me` 403 at
    // ~950ms, the exchange only starting at ~1.4s, then a 200 retry at ~7.3s).
    // Waiting for the live user costs nothing — the tree is useless without one.
    enabled: enabled && !!userDetails?.userId && !!currentUser,
    staleTime: 5 * 60 * 1000,
  });

  const useCases: UseCase[] = useMemo(() => data?.useCases ?? [], [data]);

  return {
    useCases,
    isLoadingAccessTree: isLoading,
    accessTreeError: error,
    // Lets a consumer's error state offer a calm retry without re-implementing the query.
    refetchAccessTree: refetch,
    // Drives the error state's retry-in-flight affordance (a retry is a background fetch).
    isFetchingAccessTree: isFetching,
    // True only once loaded AND at least one use case with at least one dashboard
    // is present — so the nav group renders only when there's something to show.
    hasAnyAccess: useCases.some((uc) => uc.dashboards.length > 0),
  };
}
